import { useState } from 'react'
import { Input, Modal, Select, Typography } from 'antd'

const { Text } = Typography

interface ReasonOption {
  value: string
  label: string
}

interface Props {
  open: boolean
  title: string
  /** 输入框上方的说明：为什么需要这个理由 */
  description?: React.ReactNode
  placeholder?: string
  /** 是否必须给出理由。**结构化理由也算**——选了标签就不必再打字。 */
  required?: boolean
  confirmText?: string
  danger?: boolean
  loading?: boolean
  /** 结构化理由标签（可选）。给了就渲染下拉：一次点击，不拖慢审核。 */
  reasonOptions?: ReasonOption[]
  onCancel: () => void
  onSubmit: (payload: { note: string; reasonCode?: string }) => void
}

/**
 * 审核动作的理由弹窗（发布 / 退回 / 下架共用）。
 *
 * 两条历史教训决定了它的形状：
 *
 * 1. **不要强制打字**。业务方 2026-09-17："审核能不能不弹出加理由了，影响审核速度。"
 *    实测 14 次发布填的全是"已核对"，零信息量。
 * 2. **但理由必须留下**。同一天实测 27 条人工退回里 24 条的理由是"未填说明" ——
 *    没有理由的标签只能告诉模型"这条不要"，说不出"为什么"，
 *    于是"拿人工决定当金标训练打分器"这条路直接断了。
 *
 * 所以理由拆成两层：**一次点击的结构化标签**（首选）+ 可选的补充说明。
 * 两者都不填也能提交（后端自动记事实流水），只是标签质量差一档。
 */
export default function KnowledgeReviewNoteModal({
  open,
  title,
  description,
  placeholder,
  required = true,
  confirmText = '确定',
  danger = false,
  loading = false,
  reasonOptions,
  onCancel,
  onSubmit,
}: Props) {
  const [note, setNote] = useState('')
  const [reasonCode, setReasonCode] = useState<string | undefined>(undefined)

  const trimmed = note.trim()
  // 选了结构化理由就算"给了理由"——不再逼人重复打字
  const blocked = required && !trimmed && !reasonCode

  return (
    <Modal
      open={open}
      title={title}
      okText={confirmText}
      cancelText="取消"
      okButtonProps={{ danger, disabled: blocked, loading }}
      onCancel={onCancel}
      onOk={() => onSubmit({ note: trimmed, reasonCode })}
      destroyOnHidden
      // 每次打开都从空白开始。刻意放在 afterOpenChange 回调里而不是 useEffect：
      // 在 effect 里同步 setState 会触发级联渲染（eslint react-hooks 也会拦）。
      afterOpenChange={(visible) => {
        if (visible) {
          setNote('')
          setReasonCode(undefined)
        }
      }}
    >
      {description && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          {description}
        </Text>
      )}
      {reasonOptions && reasonOptions.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">理由标签（点一下即可，是后续自动打分的依据）：</Text>
          <Select
            allowClear
            style={{ width: '100%', marginTop: 4 }}
            placeholder="选择理由（也可以不选）"
            value={reasonCode}
            options={reasonOptions}
            onChange={(value) => setReasonCode(value ?? undefined)}
          />
        </div>
      )}
      <Input.TextArea
        rows={3}
        maxLength={512}
        showCount
        value={note}
        placeholder={placeholder ?? '例如：已核对来源与现场记录'}
        onChange={(e) => setNote(e.target.value)}
      />
    </Modal>
  )
}
